import { useState, useEffect } from "react";

// ─── Icons ────────────────────────────────────────────────────────────────────
const SteamIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.029 4.524 4.524s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.497 1.009 2.454-.397.957-1.494 1.41-2.455 1.013zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z" />
  </svg>
);

const TrophyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="22" height="22">
    <path d="M6 9H4.5a2.5 2.5 0 010-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 000-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0012 0V2z" />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="18" height="18">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const ChallongeIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
    <path d="M3 3h8v8H3zm10 0h8v8h-8zM3 13h8v8H3zm13 3a2 2 0 100 4 2 2 0 000-4z" opacity=".85" />
  </svg>
);

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    open:     { label: "Åben",     color: "#22c55e" },
    upcoming: { label: "Kommende", color: "#f59e0b" },
    closed:   { label: "Lukket",   color: "#6b7280" },
    live:     { label: "LIVE",     color: "#ef4444" },
  };
  const { label, color } = map[status] || map.upcoming;
  return (
    <span style={{
      background: color + "22", color, border: `1px solid ${color}55`,
      borderRadius: 4, padding: "2px 9px", fontSize: 11, fontWeight: 700,
      letterSpacing: "0.06em", textTransform: "uppercase",
    }}>
      {label}
    </span>
  );
};

// ─── Tournament card ──────────────────────────────────────────────────────────
const TournamentCard = ({ tournament, user, onJoin }) => {
  const isMember = user?.is_member;
  const fee = tournament.fee ?? 20;
  const price = isMember ? 0 : fee;
  const alreadyJoined = user && tournament.participants?.includes(user.steamid);

  return (
    <div className="t-card">
      <div className="t-card__header">
        <div>
          <StatusBadge status={tournament.status} />
          <h3 className="t-card__title">{tournament.name}</h3>
          <p className="t-card__game">{tournament.game}</p>
        </div>
        <div className="t-card__meta">
          <div className="t-card__meta-item">
            <UserIcon />
            <span>{tournament.participant_count ?? 0}/{tournament.max_participants ?? "∞"}</span>
          </div>
        </div>
      </div>

      <div className="t-card__body">
        <div className="t-card__info-row">
          <span className="t-card__label">Dato</span>
          <span>{new Date(tournament.date).toLocaleDateString("da-DK", {
            day: "numeric", month: "long", year: "numeric",
            hour: "2-digit", minute: "2-digit"
          })}</span>
        </div>
        <div className="t-card__info-row">
          <span className="t-card__label">Format</span>
          <span>{tournament.format ?? "Single Elimination"}</span>
        </div>
        <div className="t-card__info-row">
          <span className="t-card__label">Indmeldingsgebyr</span>
          <span className={price === 0 ? "t-card__free" : "t-card__fee"}>
            {price === 0 ? "Gratis (Medlem)" : `${price} kr`}
          </span>
        </div>
      </div>

      <div className="t-card__footer">
        {tournament.challonge_url && (
          <a href={tournament.challonge_url} target="_blank" rel="noreferrer" className="btn btn--ghost">
            <ChallongeIcon /> Challonge bracket
          </a>
        )}

        {!user ? (
          <a href="/auth/steam" className="btn btn--steam">
            <SteamIcon /> Log ind for at tilmelde
          </a>
        ) : alreadyJoined ? (
          <span className="btn btn--done">✓ Tilmeldt</span>
        ) : tournament.status === "open" ? (
          <button className="btn btn--primary" onClick={() => onJoin(tournament)}>
            {price === 0 ? "Tilmeld gratis" : `Betal & Tilmeld (${price} kr)`}
          </button>
        ) : (
          <span className="btn btn--disabled">Tilmelding lukket</span>
        )}
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
export default function TournamentPage() {
  const [user, setUser] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payMsg, setPayMsg] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "success") setPayMsg("success");
    if (params.get("payment") === "cancel") setPayMsg("cancel");

    Promise.all([
      fetch("/auth/steam/me",    { credentials: "include" }).then((r) => r.json()),
      fetch("/api/tournaments",  { credentials: "include" }).then((r) => r.json()),
    ])
      .then(([userData, tournamentData]) => {
        if (!userData.error) setUser(userData);
        setTournaments(tournamentData.tournaments ?? []);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleJoin = async (tournament) => {
    const isMember = user?.is_member;
    const fee = tournament.fee ?? 20;

    if (isMember || fee === 0) {
      const res = await fetch(`/api/tournaments/${tournament.id}/join`, {
        method: "POST",
        credentials: "include",
      }).then((r) => r.json());

      if (res.ok) {
        setTournaments((prev) =>
          prev.map((t) =>
            t.id === tournament.id
              ? {
                  ...t,
                  participants: [...(t.participants ?? []), user.steamid],
                  participant_count: (t.participant_count ?? 0) + 1,
                }
              : t
          )
        );
      } else {
        alert(res.error ?? "Noget gik galt");
      }
    } else {
      const res = await fetch(`/api/tournaments/${tournament.id}/checkout`, {
        method: "POST",
        credentials: "include",
      }).then((r) => r.json());

      if (res.url) window.location.href = res.url;
      else alert(res.error ?? "Noget gik galt");
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800&family=Barlow:wght@400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --bg: #0d0f14;
          --surface: #14171f;
          --surface2: #1c2030;
          --border: #ffffff12;
          --accent: #e8b43a;
          --accent2: #4f8ef7;
          --text: #e8eaf0;
          --text2: #8b92a8;
          --green: #22c55e;
          --red: #ef4444;
          --radius: 10px;
        }

        body { background: var(--bg); color: var(--text); font-family: 'Barlow', sans-serif; min-height: 100vh; }

        .page { max-width: 960px; margin: 0 auto; padding: 40px 20px 80px; }

        .page-header {
          display: flex; align-items: flex-start; justify-content: space-between;
          margin-bottom: 48px; gap: 16px; flex-wrap: wrap;
        }
        .page-title {
          font-family: 'Barlow Condensed', sans-serif;
          font-size: clamp(36px, 6vw, 58px); font-weight: 800;
          line-height: 1; letter-spacing: -0.5px; color: var(--text);
        }
        .page-title span { color: var(--accent); }
        .page-subtitle { color: var(--text2); margin-top: 6px; font-size: 15px; }

        .user-chip {
          display: flex; align-items: center; gap: 10px;
          background: var(--surface); border: 1px solid var(--border);
          border-radius: 50px; padding: 8px 14px 8px 8px;
        }
        .user-chip img { width: 32px; height: 32px; border-radius: 50%; }
        .user-chip__name { font-weight: 600; font-size: 14px; }
        .user-chip__badge {
          font-size: 11px; font-weight: 700; letter-spacing: .05em;
          text-transform: uppercase; padding: 2px 7px; border-radius: 4px;
        }
        .user-chip__badge--member { background: #22c55e22; color: var(--green); border: 1px solid #22c55e44; }
        .user-chip__badge--guest  { background: #f59e0b22; color: #f59e0b;      border: 1px solid #f59e0b44; }
        .logout-link { font-size: 12px; color: var(--text2); text-decoration: none; margin-left: 4px; }
        .logout-link:hover { color: var(--text); }

        .banner {
          border-radius: var(--radius); padding: 14px 18px; margin-bottom: 28px;
          font-size: 14px; font-weight: 500; display: flex; align-items: center; gap: 10px;
        }
        .banner--success { background: #22c55e18; border: 1px solid #22c55e44; color: var(--green); }
        .banner--cancel  { background: #ef444418; border: 1px solid #ef444444; color: var(--red); }
        .banner--info    { background: #4f8ef718; border: 1px solid #4f8ef744; color: var(--accent2); }

        .t-grid {
          display: grid; gap: 20px;
          grid-template-columns: repeat(auto-fill, minmax(min(100%, 420px), 1fr));
        }

        .t-card {
          background: var(--surface); border: 1px solid var(--border);
          border-radius: var(--radius); overflow: hidden;
          transition: border-color .2s, transform .2s;
        }
        .t-card:hover { border-color: #ffffff28; transform: translateY(-2px); }

        .t-card__header {
          display: flex; justify-content: space-between; align-items: flex-start;
          padding: 20px 20px 0;
        }
        .t-card__title {
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 24px; font-weight: 700; margin-top: 8px; line-height: 1.15;
        }
        .t-card__game { color: var(--text2); font-size: 13px; margin-top: 3px; }
        .t-card__meta { text-align: right; }
        .t-card__meta-item { display: flex; align-items: center; gap: 5px; color: var(--text2); font-size: 13px; }

        .t-card__body {
          padding: 16px 20px; display: flex; flex-direction: column; gap: 8px;
          border-top: 1px solid var(--border); margin-top: 16px;
        }
        .t-card__info-row {
          display: flex; justify-content: space-between; align-items: center; font-size: 13.5px;
        }
        .t-card__label { color: var(--text2); }
        .t-card__free  { color: var(--green); font-weight: 600; }
        .t-card__fee   { color: var(--accent); font-weight: 600; }

        .t-card__footer {
          display: flex; align-items: center; justify-content: flex-end; gap: 10px;
          padding: 14px 20px; border-top: 1px solid var(--border); flex-wrap: wrap;
        }

        .btn {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 9px 18px; border-radius: 7px; font-size: 14px; font-weight: 600;
          cursor: pointer; border: none; text-decoration: none; transition: all .15s;
          font-family: inherit; white-space: nowrap;
        }
        .btn--primary  { background: var(--accent); color: #0d0f14; }
        .btn--primary:hover { background: #f0c050; }
        .btn--steam    { background: #1b2838; color: #c7d5e0; border: 1px solid #2a3f5f; }
        .btn--steam:hover { background: #2a3f5f; }
        .btn--ghost    { background: transparent; color: var(--text2); border: 1px solid var(--border); }
        .btn--ghost:hover { color: var(--text); border-color: #ffffff30; }
        .btn--done     { background: #22c55e18; color: var(--green); border: 1px solid #22c55e44; cursor: default; }
        .btn--disabled { background: var(--surface2); color: var(--text2); cursor: default; }

        .empty { text-align: center; padding: 80px 20px; color: var(--text2); }
        .empty svg { margin-bottom: 16px; opacity: .35; }
        .empty h3 { font-family: 'Barlow Condensed', sans-serif; font-size: 24px; color: var(--text); margin-bottom: 6px; }

        .skeleton { background: var(--surface2); border-radius: var(--radius); height: 220px; animation: pulse 1.4s infinite; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }

        .login-banner {
          background: var(--surface); border: 1px solid var(--border);
          border-radius: var(--radius); padding: 20px 24px;
          display: flex; align-items: center; justify-content: space-between;
          gap: 16px; flex-wrap: wrap; margin-bottom: 32px;
        }
        .login-banner p { color: var(--text2); font-size: 14px; }
        .login-banner strong { color: var(--text); }
      `}</style>

      <div className="page">

        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">
              <TrophyIcon /> Turneringer<span>.</span>
            </h1>
            <p className="page-subtitle">
              Gratis for medlemmer · 20 kr for gæster · Tilmelding via Challonge
            </p>
          </div>

          {user && (
            <div className="user-chip">
              {user.avatar && <img src={user.avatar} alt={user.username} />}
              <div>
                <div className="user-chip__name">{user.username}</div>
                <span className={`user-chip__badge ${user.is_member ? "user-chip__badge--member" : "user-chip__badge--guest"}`}>
                  {user.is_member ? "Medlem" : "Gæst"}
                </span>
              </div>
              <a href="/auth/steam/logout" className="logout-link">Log ud</a>
            </div>
          )}
        </div>

        {/* Betalings-bannere */}
        {payMsg === "success" && (
          <div className="banner banner--success">✓ Betaling gennemført! Du er tilmeldt turneringen.</div>
        )}
        {payMsg === "cancel" && (
          <div className="banner banner--cancel">✕ Betaling annulleret. Prøv igen når du er klar.</div>
        )}

        {/* Login prompt */}
        {!user && !loading && (
          <div className="login-banner">
            <p><strong>Log ind med Steam</strong> for at se dit gebyr og tilmelde dig turneringer.</p>
            <a href="/auth/steam" className="btn btn--steam">
              <SteamIcon /> Log ind med Steam
            </a>
          </div>
        )}

        {/* Ikke-medlem info */}
        {user && !user.is_member && (
          <div className="banner banner--info">
            💡 Du er ikke registreret som medlem. Tilmelding koster 20 kr. Kontakt en admin for at få dit medlemsnummer tilknyttet.
          </div>
        )}

        {/* Turnerings-grid */}
        {loading ? (
          <div className="t-grid">
            {[1, 2, 3].map((i) => <div key={i} className="skeleton" />)}
          </div>
        ) : tournaments.length === 0 ? (
          <div className="empty">
            <TrophyIcon />
            <h3>Ingen turneringer endnu</h3>
            <p>Hold øje med denne side — nye turneringer annonceres snart.</p>
          </div>
        ) : (
          <div className="t-grid">
            {tournaments.map((t) => (
              <TournamentCard key={t.id} tournament={t} user={user} onJoin={handleJoin} />
            ))}
          </div>
        )}

      </div>
    </>
  );
}